<section class="faq-hero">
    <div class="container">
        <div class="row align-items-center">
            <div class="col-lg-6">
                <div class="hero-content">
                    <span class="hero-badge">Help & Support</span>
                    <h1 class="hero-title">Frequently Asked Questions</h1>
                    <p class="hero-description">Find answers to common questions about our cloud services, billing, support, and more. Can't find what you're looking for? Our support team is here to help.</p>
                    
                    <div class="hero-actions">
                        <a class="btn-primary-gradient me-3" routerLink="/contact">
                            <i class="bx bx-phone"></i>
                            Contact Support
                        </a>
                        <a class="btn-outline-light" routerLink="/pricing">
                            <i class="bx bx-credit-card"></i>
                            View Pricing
                        </a>
                    </div>
                    
                    <div class="hero-features">
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>24/7 Expert Support</span>
                        </div>
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>Comprehensive Documentation</span>
                        </div>
                        <div class="feature-item">
                            <i class="bx bx-check-circle"></i>
                            <span>Knowledge Base</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="hero-image">
                    <div class="image-decoration">
                        <div class="floating-card card-1">
                            <i class="bx bx-help-circle"></i>
                            <span>Quick Answers</span>
                        </div>
                        <div class="floating-card card-2">
                            <i class="bx bx-support"></i>
                            <span>Live Support</span>
                        </div>
                        <div class="floating-card card-3">
                            <i class="bx bx-book"></i>
                            <span>Guides & Tutorials</span>
                        </div>
                    </div>
                    <img src="assets/img/faq-hero.png" alt="FAQ" class="img-fluid main-image">
                </div>
            </div>
        </div>
    </div>
    <div class="hero-background-elements">
        <div class="bg-element element-1"></div>
        <div class="bg-element element-2"></div>
        <div class="bg-element element-3"></div>
    </div>
</section>

<section class="faq-section ptb-100">
    <div class="container">
        <div class="section-title">
            <span class="section-badge">FAQ</span>
            <h2>Common Questions & Answers</h2>
            <p>Explore our comprehensive FAQ section to find quick answers to frequently asked questions about our services.</p>
        </div>

        <div class="faq-accordion">
            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>What payment methods do you accept?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>We accept all major credit cards including Visa, MasterCard, and American Express. We also support bank transfers and PayPal payments. All payments are processed securely through our PCI-compliant payment gateway.</p>
                </div>
            </div>

            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>How do I upgrade or downgrade my plan?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>You can easily upgrade or downgrade your plan at any time through your account dashboard. Changes are applied immediately, and you'll only be charged the prorated difference for the remainder of your billing cycle.</p>
                </div>
            </div>

            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>What is your uptime guarantee?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>We guarantee 99.9% uptime for all our cloud services. In the rare event of downtime exceeding our SLA, you'll receive service credits as compensation. Our infrastructure is built with redundancy to ensure maximum availability.</p>
                </div>
            </div>

            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>How quickly can I get support?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>Our support team is available 24/7/365. Response times vary by plan: Essential plan users receive responses within 8 hours, Professional plan within 4 hours, and Enterprise plan within 1 hour. Phone support is available for Professional and Enterprise plans.</p>
                </div>
            </div>

            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>Can I migrate from another provider?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>Yes, we offer free migration services for all our customers moving from other providers. Our expert team will handle the entire process with zero downtime. Simply contact our support team to schedule your migration.</p>
                </div>
            </div>

            <div class="accordion-item">
                <div class="accordion-header">
                    <h3>Do you offer discounts for long-term commitments?</h3>
                    <div class="accordion-icon">
                        <i class="bx bx-plus"></i>
                    </div>
                </div>
                <div class="accordion-content">
                    <p>Yes, we offer significant discounts for annual and biennial commitments. Annual plans receive a 15% discount, while biennial plans receive a 25% discount. These discounts are automatically applied at checkout when you select longer billing cycles.</p>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="features-section ptb-100">
    <div class="container">
        <div class="section-title">
            <span class="section-badge">Support Resources</span>
            <h2>Additional Help Resources</h2>
            <p>Explore our comprehensive support resources to get the most out of our services.</p>
        </div>

        <div class="row">
            <div class="col-lg-4 col-md-6 mb-4">
                <div class="feature-card">
                    <div class="feature-header">
                        <div class="feature-icon">
                            <i class="bx bx-book"></i>
                        </div>
                        <div class="feature-badge">Documentation</div>
                    </div>
                    <h4>Knowledge Base</h4>
                    <p>Comprehensive guides, tutorials, and documentation to help you get started and master our platform.</p>
                    <ul class="feature-list">
                        <li><i class="bx bx-check"></i> Getting Started Guides</li>
                        <li><i class="bx bx-check"></i> API Documentation</li>
                        <li><i class="bx bx-check"></i> Best Practices</li>
                        <li><i class="bx bx-check"></i> Troubleshooting</li>
                    </ul>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 mb-4">
                <div class="feature-card">
                    <div class="feature-header">
                        <div class="feature-icon">
                            <i class="bx bx-video"></i>
                        </div>
                        <div class="feature-badge">Tutorials</div>
                    </div>
                    <h4>Video Tutorials</h4>
                    <p>Step-by-step video guides covering everything from basic setup to advanced configurations.</p>
                    <ul class="feature-list">
                        <li><i class="bx bx-check"></i> Setup Walkthroughs</li>
                        <li><i class="bx bx-check"></i> Feature Demos</li>
                        <li><i class="bx bx-check"></i> Use Case Examples</li>
                        <li><i class="bx bx-check"></i> Expert Tips</li>
                    </ul>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 mb-4">
                <div class="feature-card">
                    <div class="feature-header">
                        <div class="feature-icon">
                            <i class="bx bx-support"></i>
                        </div>
                        <div class="feature-badge">Community</div>
                    </div>
                    <h4>Community Support</h4>
                    <p>Connect with other users, share experiences, and get help from our community of experts.</p>
                    <ul class="feature-list">
                        <li><i class="bx bx-check"></i> User Forums</li>
                        <li><i class="bx bx-check"></i> Expert Q&A</li>
                        <li><i class="bx bx-check"></i> Code Samples</li>
                        <li><i class="bx bx-check"></i> Community Events</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="faq-contact-section ptb-100">
    <div class="container">
        <div class="row align-items-center">
            <div class="col-lg-6">
                <div class="contact-content">
                    <div class="section-title">
                        <span class="section-badge">Still Need Help?</span>
                        <h2>Can't Find Your Answer?</h2>
                        <p>Our support experts are ready to assist you with any questions or issues you may have. Get in touch with us for personalized assistance.</p>
                    </div>
                    
                    <div class="contact-features">
                        <div class="contact-feature-item">
                            <i class="bx bx-phone"></i>
                            <div class="contact-feature-content">
                                <h5>Phone Support</h5>
                                <p>Speak directly with our experts</p>
                            </div>
                        </div>
                        <div class="contact-feature-item">
                            <i class="bx bx-chat"></i>
                            <div class="contact-feature-content">
                                <h5>Live Chat</h5>
                                <p>Instant assistance available 24/7</p>
                            </div>
                        </div>
                        <div class="contact-feature-item">
                            <i class="bx bx-envelope"></i>
                            <div class="contact-feature-content">
                                <h5>Email Support</h5>
                                <p>Get detailed responses within hours</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="contact-actions">
                        <a class="btn-primary-gradient me-3" routerLink="/contact">
                            <i class="bx bx-phone"></i>
                            Contact Support Team
                        </a>
                        <a class="btn-outline-light" routerLink="/sign-up">
                            <i class="bx bx-user-plus"></i>
                            Start Free Trial
                        </a>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <div class="contact-image">
                    <img src="assets/img/faq-contact.png" alt="Contact Support" class="img-fluid">
                    <div class="floating-elements">
                        <div class="floating-badge badge-1">
                            <i class="bx bx-support"></i>
                            <span>24/7 Support</span>
                        </div>
                        <div class="floating-badge badge-2">
                            <i class="bx bx-time"></i>
                            <span>&lt; 1 Hour Response</span>
                        </div>
                        <div class="floating-badge badge-3">
                            <i class="bx bx-check-circle"></i>
                            <span>Expert Assistance</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="contact-background-elements">
        <div class="bg-element element-1"></div>
        <div class="bg-element element-2"></div>
    </div>
</section>